import { useBreakpointMinLg } from '@kk/shared/hooks/useBreakpoint';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { useTheme } from '#ui/hooks/useTheme.js';
import Icon, { type IconProps, type IconType } from '../Icon';
import Spinner from '../Spinner';
import { buttonVariants, type ButtonTheme as Theme } from './index';

interface CommonLinkButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  icon?: IconType;
  iconProps?: IconProps;
  rightAligned?: boolean;
  compact?: boolean;
  inverted?: boolean;
  className?: string;
  preload?: number;
  subtle?: boolean;
  theme?: Theme;
  children?: React.ReactNode;
}

type HrefProps = {
  href: string;
  target?: React.HTMLAttributeAnchorTarget;
  to?: never;
};

type ToProps = {
  to: string;
  href?: never;
  target?: never;
};

type NoProps = {
  to?: never;
  target?: never;
  href?: never;
};

export type LinkButtonProps = CommonLinkButtonProps &
  (HrefProps | ToProps | NoProps);

const defaultLinkClasses = clsx(
  'flex cursor-pointer items-center rounded border-none w-content',
  'hover:text-blue-40  hover:underline active:text-blue-110',
);

export function LinkButton(props: LinkButtonProps) {
  const isKunde = useTheme() === 'kunde';
  const minLg = useBreakpointMinLg();
  const {
    label,
    compact = isKunde ? minLg : undefined, // default compact styling for sizes above tablet
    inverted,
    rightAligned,
    icon,
    iconProps,
    loading,
    to,
    href,
    onClick,
    className,
    subtle,
    theme,
    children,
    ...rest
  } = props;

  const classes = theme
    ? twMerge(
        buttonVariants({
          theme,
          compact,
          inverted,
          rightAligned,
          loading,
          hasLabel: !!label,
          hasIcon: !!icon,
        }),
        className,
      )
    : twMerge(
        clsx(
          defaultLinkClasses,
          subtle
            ? 'text-neutral-110 active:text-blue-110 active:no-underline'
            : 'font-medium text-blue-60 ',
          'disabled:text-neutral-60 aria-disabled:text-neutral-60',
          compact
            ? [
                'typ-link-xs',
                rightAligned
                  ? { 'pr-1 pl-2': icon && !loading }
                  : { 'pr-2 pl-1': icon && !loading },
              ]
            : [
                'typ-link',
                rightAligned
                  ? { 'pr-2 pl-4': icon && !loading }
                  : { 'pr-4 pl-2': icon && !loading },
              ],
          rightAligned && 'flex-row-reverse',
        ),
        className,
      );

  const buttonContent = loading ? (
    <Spinner
      className={clsx(
        'flex items-center justify-center',
        compact ? 'px-4' : 'px-1',
      )}
      size={compact ? 'small' : 'medium'}
    />
  ) : (
    <>
      {icon ? (
        <Icon
          size={compact || subtle ? 'tiny' : 'small'}
          {...(iconProps ? iconProps : {})}
          type={icon}
          className={clsx(
            'flex-none self-center',
            rightAligned
              ? compact
                ? 'ml-2'
                : 'laane:ml-4 kunde:mr-2'
              : compact
                ? 'mr-2'
                : 'laane:mr-4 kunde:mr-2',
            iconProps?.className,
          )}
        />
      ) : null}
      <span className="whitespace-nowrap break-all">{label || children}</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={clsx(classes, { 'pointer-events-none': rest.disabled })}
        rel="nofollow"
        {...rest}
        tabIndex={rest.disabled ? -1 : undefined}
        aria-disabled={rest.disabled ? 'true' : undefined}
      >
        {buttonContent}
      </a>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={clsx(classes, { 'pointer-events-none': rest.disabled })}
        {...rest}
        tabIndex={rest.disabled ? -1 : undefined}
        aria-disabled={rest.disabled ? 'true' : undefined}
      >
        {buttonContent}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={classes} {...rest}>
      {buttonContent}
    </button>
  );
}

export default LinkButton;
